import { Controller } from "@hotwired/stimulus";

/**
 * Click-toggled dropdown (navbar profile menu).
 * Usage:
 *   <div data-controller="dropdown" class="relative">
 *     <button data-action="click->dropdown#toggle" data-dropdown-target="trigger" aria-expanded="false">Trigger</button>
 *     <div data-dropdown-target="menu" class="hidden">Menu content</div>
 *   </div>
 *
 * Closes on click outside, Escape (focus back to the trigger), focus leaving the dropdown
 * and when another dropdown opens (`dropdown:close-others`). Also closes on
 * `turbo:before-cache`: Turbo snapshots the page as it is, and Back/Forward would
 * otherwise restore it with the menu open and the controller thinking it is closed.
 */
export default class extends Controller<HTMLElement> {
  static targets = ["trigger", "menu"];

  declare readonly triggerTarget: HTMLElement;
  declare readonly menuTarget: HTMLElement;

  private isOpen = false;

  private readonly handleClickOutside = (event: MouseEvent) => {
    if (!this.element.contains(event.target as Node)) this.close();
  };

  private readonly handleEscape = (event: KeyboardEvent) => {
    if (event.key !== "Escape") return;

    this.close();
    this.triggerTarget.focus();
  };

  private readonly handleFocusOut = (event: FocusEvent) => {
    const next = event.relatedTarget as Node | null;
    // relatedTarget is null for clicks on non-focusable content - click outside handles those
    if (next && !this.element.contains(next)) this.close();
  };

  private readonly handleCloseOthers = (event: Event) => {
    if ((event as CustomEvent).detail?.source !== this.element) this.close();
  };

  private readonly handleBeforeCache = () => this.close();

  connect() {
    window.addEventListener("dropdown:close-others", this.handleCloseOthers);
    document.addEventListener("turbo:before-cache", this.handleBeforeCache);
    this.element.addEventListener("focusout", this.handleFocusOut);
    this.close();
  }

  disconnect() {
    this.close();
    window.removeEventListener("dropdown:close-others", this.handleCloseOthers);
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache);
    this.element.removeEventListener("focusout", this.handleFocusOut);
  }

  toggle(event: Event) {
    event.preventDefault();

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    window.dispatchEvent(new CustomEvent("dropdown:close-others", { detail: { source: this.element } }));

    this.isOpen = true;
    this.menuTarget.classList.remove("hidden");
    this.triggerTarget.setAttribute("aria-expanded", "true");

    document.addEventListener("click", this.handleClickOutside);
    document.addEventListener("keydown", this.handleEscape);
  }

  close() {
    this.isOpen = false;
    this.menuTarget.classList.add("hidden");
    this.triggerTarget.setAttribute("aria-expanded", "false");

    document.removeEventListener("click", this.handleClickOutside);
    document.removeEventListener("keydown", this.handleEscape);
  }

  // Menu links close the dropdown before Turbo navigates
  closeOnClick() {
    this.close();
  }
}
